import { ActualityHqWithCongestionsRow } from '@warebee/shared/dataset-queries';
import classNames from 'classnames';
import React from 'react';
import { useRecoilValue } from 'recoil';
import {
  EventGrouping,
  EventIssueTiming,
} from '../constants/actuality.event.config';
import {
  ProcessedEventIssue,
  useEventIssues,
  useEventIssuesByTiming,
} from '../hooks/useEventConfig';
import { actualityVisibleEventIssuesSelector } from '../store/actuality.state';
import EventIssue from './EventIssue';

export interface ConfigurableEventIssuesProps {
  event: ActualityHqWithCongestionsRow;
  grouping?: EventGrouping;
  timing?: EventIssueTiming;
  className?: string;
  separateByTiming?: boolean;
}

const ConfigurableEventIssues: React.FC<ConfigurableEventIssuesProps> = ({
  event,
  grouping = 'all',
  timing,
  className,
  separateByTiming = false,
}) => {
  const visibleEventIssues = useRecoilValue(
    actualityVisibleEventIssuesSelector,
  );
  const singleTimingIssues = useEventIssues(
    event,
    grouping,
    timing,
    visibleEventIssues,
  );
  const issuesByTiming = useEventIssuesByTiming(
    event,
    grouping,
    visibleEventIssues,
  );

  const convertToEventIssueFormat = (issues: ProcessedEventIssue[]) => {
    return issues.map(issue => ({
      title: issue.title,
      value: issue.value,
      unitOfMeasure: issue.unitOfMeasure,
      status: issue.severity,
    }));
  };

  // If a specific timing is requested or not separating by timing
  if (timing || !separateByTiming) {
    const issues = issuesByTiming.all;
    const timingToUse = timing || 'before'; // Default timing if not specified

    if (issues.length === 0) return null;

    return (
      <div className={classNames('configurable-event-issues', className)}>
        <EventIssue
          timing={timingToUse}
          issues={convertToEventIssueFormat(issues)}
        />
      </div>
    );
  }

  // Render issues separated by timing
  return (
    <div className={classNames('configurable-event-issues', className)}>
      {issuesByTiming.before.length > 0 && (
        <EventIssue
          timing="before"
          issues={convertToEventIssueFormat(issuesByTiming.before)}
        />
      )}

      {issuesByTiming.during.length > 0 && (
        <EventIssue
          timing="during"
          issues={convertToEventIssueFormat(issuesByTiming.during)}
        />
      )}

      {issuesByTiming.after.length > 0 && (
        <EventIssue
          timing="after"
          issues={convertToEventIssueFormat(issuesByTiming.after)}
        />
      )}
    </div>
  );
};

export default ConfigurableEventIssues;
